import { useState, useMemo, useEffect, useRef } from 'react';
import { ColumnVisibilityModels } from '../../../../../../components/Table/ColumnVisibilityModels';
import { UseColumnManagementProps, ColumnManagementReturn } from '../types';
import { chemicalRestrictedColumns } from '../../../constants';
import { useNavigationStore } from '../../../../../../store';

export function useColumnManagement({
  view,
  tab,
  isEvidence,
  isMapping,
  mappingContent,
  restrictChemical,
}: UseColumnManagementProps): ColumnManagementReturn {
  const hasLoadedFromSavedRef = useRef(false);
  const previousSearchItemsCountRef = useRef<number>(0);
  const previousUrlIdentsRef = useRef<string>('');

  const searchItems = useNavigationStore(state => state.searchItems);

  const urlParams = new URLSearchParams(window.location.search);
  const urlIdents = urlParams.get('idents') || '';
  const urlView = urlParams.get('view') || '';

  const selectColumnVisibilityModel = useMemo(() => {
    const fnColumnVisibilityModel =
      isMapping && mappingContent?.isActive
        ? () => {
            const model = ColumnVisibilityModels.getByMappingName(
              mappingContent.columVisibilityModelKey,
              isEvidence,
            );
            return model;
          }
        : () => {
            return ColumnVisibilityModels.getByViewTab(
              view,
              tab,
              isEvidence,
              searchItems,
              urlIdents,
              urlView,
            );
          };

    const fnColumnVisibilityModelWithRestrictions = () => {
      const originalList = fnColumnVisibilityModel();
      if (!originalList) return undefined;

      const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

      if (restrictedList.length === 0) {
        return originalList;
      }

      return Object.fromEntries(
        Object.entries(originalList).filter(([key]) => !restrictedList.includes(key)),
      );
    };

    return fnColumnVisibilityModelWithRestrictions;
  }, [
    view,
    tab,
    mappingContent?.columVisibilityModelKey,
    restrictChemical,
    isMapping,
    isEvidence,
    searchItems,
    urlIdents,
    urlView,
  ]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    return selectColumnVisibilityModel();
  });

  const [columnOrder, setColumnOrder] = useState<string[] | undefined>(undefined);

  const applyConditionalColumnChanges = (currentModel: any, newBaseModel: any) => {
    if (!currentModel || !newBaseModel) return newBaseModel;

    const conditionalColumns = ['diseaseName', 'geneSymbol', 'variantStrID', 'strID'];

    const updatedModel = { ...currentModel };
    let hasChanges = false;

    conditionalColumns.forEach(column => {
      if (newBaseModel[column] !== undefined && currentModel[column] !== newBaseModel[column]) {
        updatedModel[column] = newBaseModel[column];
        hasChanges = true;
      }
    });

    return hasChanges ? updatedModel : currentModel;
  };

  useEffect(() => {
    const newBaseModel = selectColumnVisibilityModel();
    const currentSearchItemsCount = searchItems.length;
    const previousCount = previousSearchItemsCountRef.current;
    const previousUrlIdents = previousUrlIdentsRef.current;

    if (!newBaseModel) {
      setColumnVisibilityModel(newBaseModel);
      setColumnOrder(undefined);
      hasLoadedFromSavedRef.current = false;
      previousSearchItemsCountRef.current = currentSearchItemsCount;
      previousUrlIdentsRef.current = urlIdents;
      return;
    }

    if (!hasLoadedFromSavedRef.current) {
      setColumnVisibilityModel(newBaseModel);
      const orderedKeys = Object.keys(newBaseModel);
      const visibleKeys = orderedKeys.filter(key => newBaseModel[key] === true);
      setColumnOrder(visibleKeys);
      previousSearchItemsCountRef.current = currentSearchItemsCount;
      previousUrlIdentsRef.current = urlIdents;
      return;
    }

    const searchItemsChanged = previousCount !== currentSearchItemsCount && currentSearchItemsCount > 0;
    const urlIdentsChanged = previousUrlIdents !== urlIdents && urlIdents !== '';

    if (searchItemsChanged || urlIdentsChanged) {
      setColumnVisibilityModel(currentModel => {
        const updatedModel = applyConditionalColumnChanges(currentModel, newBaseModel);

        if (updatedModel !== currentModel) {
          const orderedKeys = Object.keys(updatedModel);
          const visibleKeys = orderedKeys.filter(key => updatedModel[key] === true);
          setColumnOrder(visibleKeys);
        }

        return updatedModel;
      });
    }

    previousSearchItemsCountRef.current = currentSearchItemsCount;
    previousUrlIdentsRef.current = urlIdents;
  }, [
    selectColumnVisibilityModel,
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent?.isActive,
    mappingContent?.columVisibilityModelKey,
    searchItems,
    urlIdents,
    urlView,
  ]);

  const resetToDefault = () => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence, searchItems, urlIdents, urlView);

    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

    const defaultModel =
      baseDefaultModel && restrictedList.length > 0
        ? Object.fromEntries(
            Object.entries(baseDefaultModel).filter(([key]) => !restrictedList.includes(key)),
          )
        : baseDefaultModel;

    if (defaultModel) {
      setColumnVisibilityModel(defaultModel);
      const defaultColumnOrder = Object.keys(defaultModel).filter(key => defaultModel[key] === true);
      setColumnOrder(defaultColumnOrder);
    }

    hasLoadedFromSavedRef.current = false;
  };

  return {
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnOrder,
    setColumnOrder,
    hasLoadedFromSavedRef,
    resetToDefault,
  };
}
